// src/services/authService.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { auth, db, COLLECTIONS } from './firebase';

const googleProvider = new GoogleAuthProvider();

export class AuthService {
  // Register with Email and Password
  static async registerWithEmail(email, password, displayName) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update profile with display name
      await updateProfile(user, { displayName });
      
      // Create user document in Firestore
      await setDoc(doc(db, COLLECTIONS.USERS, user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: displayName,
        authProvider: 'email',
        profileCompleted: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return user;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  // Sign in with Email and Password
  static async signInWithEmail(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Ensure user document exists
      await this.ensureUserDocumentExists(user);
      
      return user;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  // Sign in with Google
  static async signInWithGoogle() {
  try {
    console.log('Attempting Google sign in with popup...');
    const result = await signInWithPopup(auth, googleProvider);
    console.log('Google sign in popup successful:', result);
    
    const user = result.user;
    console.log('User from Google sign in:', user);
    
    // Ensure user document exists
    await this.ensureUserDocumentExists(user);
    console.log('User document ensured');
    
    return user;
  } catch (error) {
    console.error('Google sign in error details:', error);
    
    // Handle specific Google sign in errors
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in was cancelled');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error('Pop-up was blocked by your browser. Please enable pop-ups and try again.');
    } else if (error.code === 'auth/cancelled-popup-request') {
      throw new Error('Only one sign-in popup allowed at a time');
    }
    
    throw this.handleAuthError(error);
  }
}

  // Ensure user document exists in Firestore
  static async ensureUserDocumentExists(user) {
    try {
      const userDocRef = doc(db, COLLECTIONS.USERS, user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        // Document doesn't exist, create it
        const authProvider = user.providerData?.[0]?.providerId === 'google.com' ? 'google' : 'email';
        
        await setDoc(userDocRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || '',
          authProvider: authProvider,
          profileCompleted: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        console.log('User document created for existing user:', user.uid);
      }
    } catch (error) {
      console.error('Error ensuring user document exists:', error);
      // Don't throw here as this is a background operation
    }
  }

  // Send Password Reset Email
  static async sendPasswordReset(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true, message: 'Password reset email sent successfully' };
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  // Sign Out
  static async signOut() {
    try {
      await signOut(auth);
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  // Get Current User
  static getCurrentUser() {
    return auth.currentUser;
  }

  // Auth State Observer
  static onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Ensure user document exists whenever auth state changes
        await this.ensureUserDocumentExists(user);
      }
      callback(user);
    });
  }

  // Get User Profile
  static async getUserProfile(uid) {
    try {
      const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, uid));
      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() };
      } else {
        // If document doesn't exist and we have a current user, create it
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.uid === uid) {
          await this.ensureUserDocumentExists(currentUser);
          // Try to get the document again
          const newUserDoc = await getDoc(doc(db, COLLECTIONS.USERS, uid));
          return newUserDoc.exists() ? { id: newUserDoc.id, ...newUserDoc.data() } : null;
        }
        return null;
      }
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  }

  // Update User Profile - Now uses setDoc with merge to handle non-existent documents
  static async updateUserProfile(uid, profileData) {
    try {
      const userDocRef = doc(db, COLLECTIONS.USERS, uid);
      
      // Use setDoc with merge option to create document if it doesn't exist
      await setDoc(userDocRef, {
        ...profileData,
        profileCompleted: true,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      return { success: true };
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  }

  // Create or Update User Profile (alternative method)
  static async createOrUpdateUserProfile(uid, profileData) {
    try {
      const userDocRef = doc(db, COLLECTIONS.USERS, uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        // Document exists, update it
        await updateDoc(userDocRef, {
          ...profileData,
          profileCompleted: true,
          updatedAt: serverTimestamp()
        });
      } else {
        // Document doesn't exist, create it
        const currentUser = auth.currentUser;
        await setDoc(userDocRef, {
          uid: uid,
          email: currentUser?.email || '',
          displayName: currentUser?.displayName || '',
          authProvider: currentUser?.providerData?.[0]?.providerId === 'google.com' ? 'google' : 'email',
          ...profileData,
          profileCompleted: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      return { success: true };
    } catch (error) {
      console.error('Error creating/updating user profile:', error);
      throw error;
    }
  }

  // Get Users in Same School/District for Sharing
  static async getUsersForSharing(currentUserProfile) {
    try {
      let queries = [];
      
      if (currentUserProfile.institution === 'School' && currentUserProfile.schoolName) {
        // Get users from same school
        queries.push(
          query(
            collection(db, COLLECTIONS.USERS),
            where('schoolName', '==', currentUserProfile.schoolName),
            where('profileCompleted', '==', true)
          )
        );
      }
      
      if (currentUserProfile.district) {
        // Get users from same district
        queries.push(
          query(
            collection(db, COLLECTIONS.USERS),
            where('district', '==', currentUserProfile.district),
            where('profileCompleted', '==', true)
          )
        );
      }
      
      const results = [];
      const userIds = new Set();
      
      for (const q of queries) {
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach(doc => {
          if (doc.id !== currentUserProfile.uid && !userIds.has(doc.id)) {
            userIds.add(doc.id);
            const userData = doc.data();
            results.push({ 
              id: doc.id, 
              ...userData, 
              shareType: userData.schoolName === currentUserProfile.schoolName ? 'School' : 'District'
            });
          }
        });
      }
      
      return results;
    } catch (error) {
      console.error('Error getting users for sharing:', error);
      throw error;
    }
  }

  // Migrate existing user (utility function for one-time migration)
  static async migrateExistingUser(user) {
    try {
      await this.ensureUserDocumentExists(user);
      console.log('Successfully migrated user:', user.uid);
    } catch (error) {
      console.error('Error migrating user:', error);
      throw error;
    }
  }

  // Batch migrate all authenticated users (admin function)
  static async batchMigrateUsers() {
    try {
      // This would typically be called from an admin interface
      // For now, it just ensures the current user is migrated
      const currentUser = auth.currentUser;
      if (currentUser) {
        await this.ensureUserDocumentExists(currentUser);
      }
    } catch (error) {
      console.error('Error batch migrating users:', error);
      throw error;
    }
  }

  // Handle Authentication Errors
  static handleAuthError(error) {
    let message = 'An authentication error occurred';
    
    switch (error.code) {
      case 'auth/user-not-found':
        message = 'No user found with this email address';
        break;
      case 'auth/wrong-password':
        message = 'Incorrect password';
        break;
      case 'auth/email-already-in-use':
        message = 'An account with this email already exists';
        break;
      case 'auth/weak-password':
        message = 'Password should be at least 6 characters';
        break;
      case 'auth/invalid-email':
        message = 'Invalid email address';
        break;
      case 'auth/network-request-failed':
        message = 'Network error. Please check your connection';
        break;
      case 'auth/too-many-requests':
        message = 'Too many failed attempts. Please try again later';
        break;
      case 'auth/popup-closed-by-user':
        message = 'Sign-in popup was closed before completion';
        break;
      case 'permission-denied':
        message = 'Permission denied. Please contact support.';
        break;
      case 'not-found':
        message = 'User profile not found. Creating new profile...';
        break;
      default:
        message = error.message || 'Authentication failed';
    }
    
    return new Error(message);
  }
}